import { Stack } from '@mantine/core';
import { Title } from '@repo/foundations';
import { sortBy } from 'lodash-es';
import { useMemo } from 'react';

import type { AutomationJob } from '../../../api/types/models/automation/automationJob';
import type { ActivityItem } from './AutomationJobActivityItem';
import { AutomationJobActivityItem } from './AutomationJobActivityItem';

interface AutomationJobActivityProps {
	automationJob: AutomationJob;
}

export function AutomationJobActivity({
	automationJob,
}: AutomationJobActivityProps) {
	const startedAt = automationJob.started_at;
	const successAt = automationJob.success_at;
	const failedAt = automationJob.error_at;
	const reviewStartedAt = automationJob.review_started_at;
	const reviewCompletedAt = automationJob.review_completed_at;
	const discardedAt = automationJob.discarded_at;
	const displayRunningStatus =
		startedAt && !successAt && !reviewStartedAt && !discardedAt;

	const jobStatuses = useMemo(() => {
		const statusesToDisplay: ActivityItem[] = [];

		if (displayRunningStatus) {
			statusesToDisplay.push({
				iconName: 'refresh',
				iconColor: 'icon/info/default',
				timestamp: startedAt,
				message: 'Automation running',
				order: 1,
			});
		} else if (!displayRunningStatus) {
			statusesToDisplay.push({
				iconName: 'refresh',
				iconColor: 'icon/secondary/default',
				timestamp: startedAt,
				message: 'Automation ran',
				order: 1,
			});
		}

		if (failedAt) {
			statusesToDisplay.push({
				iconName: 'alertCircleFilled',
				iconFillColor: 'icon/critical/default',
				timestamp: failedAt,
				message: 'Automation run failed',
				order: 1,
			});
		}

		if (reviewStartedAt) {
			statusesToDisplay.push({
				iconName: 'alertTriangleFilled',
				iconFillColor: 'icon/warning/default',
				timestamp: reviewStartedAt,
				message: 'Pending review',
				order: 1,
			});
			statusesToDisplay.push({
				iconName: 'infoCircle',
				iconColor: 'icon/secondary/default',
				timestamp: reviewStartedAt,
				message: 'Review and approve AI updates to finish the automation',
				order: 2,
			});
		}

		if (reviewStartedAt && reviewCompletedAt) {
			statusesToDisplay.push({
				iconName: 'refresh',
				iconColor: 'icon/info/default',
				timestamp: reviewCompletedAt,
				message: 'Syncing approved updates',
				order: 1,
			});
		}

		if (successAt) {
			statusesToDisplay.push({
				iconName: 'circleCheckFilled',
				iconFillColor: 'icon/success/default',
				timestamp: successAt,
				message: 'Automation completed',
				order: 1,
			});
		}

		if (discardedAt) {
			statusesToDisplay.push({
				iconName: 'circleXFilled',
				iconColor: 'icon/primary/disabled',
				timestamp: discardedAt,
				message: 'Automation discarded',
				order: 1,
			});
		}

		return statusesToDisplay;
	}, [
		displayRunningStatus,
		startedAt,
		failedAt,
		reviewStartedAt,
		reviewCompletedAt,
		successAt,
		discardedAt,
	]);

	// join automation status and activity logs;
	// activity logs will follow at a later time
	const activities = useMemo(
		() => sortBy([...(jobStatuses ?? [])], ['timestamp', 'order']),
		[jobStatuses]
	);

	return (
		<Stack spacing={0} pb="xl">
			<Title size="md" pb="md">
				Activity
			</Title>
			{activities.map((activity, idx) => (
				<AutomationJobActivityItem
					key={activity.timestamp + activity.message}
					activityItem={activity}
					showLine={idx !== activities.length - 1}
				/>
			))}
		</Stack>
	);
}
